import store from '@/store/index'
import { maintenanceAxios } from '@/plugins/axios.js'
import { format } from 'date-fns'
import { availablePathNamesForParentMember } from '@/router/guard/permission'

export const beforeEachGuard = async (to, from, next) => {
  console.log('----beforeEach called')
  const now = new Date()
  const timeStamp = now.getTime()
  const prettyNow = format(now, 'M月d日 HH:mm:ss')
  // キャッシュされないように、クエリにタイムスタンプを付与
  // ref: https://stackoverflow.com/questions/49263559/using-javascript-axios-fetch-can-you-disable-browser-cache
  const url = `${process.env.VUE_APP_S3_MAINTENANCE_URL}?timestamp=${timeStamp}`
  const maintenanceInfo = await maintenanceAxios.get(url)
    .then((res) => {
      const newVal = Object.assign({}, res.data)
      newVal.now = prettyNow
      return newVal
    })
    .catch((err) => {
      console.warn('err', err)
      console.warn('err.response', err.response)
      return null
    })
  store.commit('maintenance/setMaintenanceInfo', maintenanceInfo)
  console.log('got info in beforeEach', maintenanceInfo, prettyNow)

  // メンテナンス情報を取得できなかった場合
  // オフラインやバックグラウンド状態のときにナビゲーションが起こると、不完全な画面が表示されてしまうため、
  // ナビゲーションしない
  if (!maintenanceInfo) {
    console.log('beforeEach. maintenanceInfoがnullの時は画面遷移しない next(false)', 'maintenanceInfo: ', maintenanceInfo)
    next(false)
    return
  }

  // メンテナンス画面へのナビゲーション
  if (to.path === '/maintenance') {
    if (maintenanceInfo.isInMaintenance) {
      console.log('beforeEach. 行先がメンテナンスで、isInMaintenance:trueの場合、そのままメンテナンスへ')
      next()
    } else {
      console.log('beforeEach. 行先がメンテナンスで、isInMaintenance:falseの場合、トップへ')
      next('/')
    }
    return
  }

  // メンテナンス中であればメンテナンス画面へ
  if (maintenanceInfo.isInMaintenance) {
    next('/maintenance')
    console.log('beforeEach. メンテナンスへ')
    return
  }

  const isLoginRequired = to.matched.some(record => record.meta.isLoginRequired)
  // ログイン状態が必要なければそのままアクセス
  if (!isLoginRequired) {
    next()
    return
  }

  // 利用停止ユーザーはログアウトする
  const isUnsubscribe = await store.dispatch('auth/updateUser')
  if (!isUnsubscribe){
    store.commit('notify/set', {message: 'もう一度ログインしてください。'})
    await store.dispatch('auth/logout')
    next('/auth/login')
    return
  }
  
  const user = store.state.auth.user
  if (!user) {
    next('/auth/login')
    return
  }

  // === 保護者ユーザのアクセス制御 ===
  // 保護者ユーザは設定画面には無条件でアクセスできる
  if (store.getters['auth/isParentMember']) {

    if (to.path === '/settings') {
      next()
      return
    }

    // 保護者ユーザかつ、予定表にアクセスできない場合、無限ループしないように設定画面にリダイレクト
    if (!store.getters['auth/canAccessEvents']) {
      next('/settings')
      return
    }

    // 自分のgoalListまたはmandalaEvaluationに遷移できないように
    if ((to.name === 'goalList' || to.name === 'mandalaEvaluation') && to.params.id === user.id) {
      next(false)
      return
    }

    // 保護者は予定表と設定画面のみにアクセス可能なので、それ以外のパスにアクセスした場合は予定表へリダイレクト
    if (!availablePathNamesForParentMember.includes(to.name)) {
      if (to.path === '/') {
        next('/events')
        return
      }
      next(false)
      return
    }
  }

  // === 保護者ユーザのアクセス制御ここまで ===

  if (to.path === '/') {
    console.log('matched to home')
    if (user.homeView === 'member_list') {
      next('/members')
    } else {
      next(`/members/${user.id}`)
    }
    return
  }

  // === profile画面の制御 ===
  if (to.matched.some(record => record.path === '/members/:id/profile')) {
    // 全ユーザは自分のprofile画面にはアクセス可能
    if (to.params.id === user.id) {
      return next()
    }
    // 管理者は全ユーザのprofile画面(編集画面を除く)にアクセス可能
    if (store.getters['auth/isManager']) {
      // 編集画面paths(最終進学先はアクセスできるので除いている)
      const editPaths = [
        '/members/:id/profile/emergency_contacts',
        '/members/:id/profile/past_departments',
        '/members/:id/profile/path_surveys/edit',
        '/members/:id/profile/path_activities/add',
        '/members/:id/profile/path_activities/:pathActivityId',
        '/members/:id/profile/academic_records/add',
        '/members/:id/profile/academic_records/:academicRecordId',
        '/members/:id/profile/body_measurements/add',
        '/members/:id/profile/body_measurements/:bodyMeasurementId'
      ]
      const isEditPath = editPaths.some(editPath => to.matched.some(record => record.path === editPath))
      if (isEditPath) {
        // 編集画面にアクセスしようとした場合、該当メンバーのマイデータ画面にリダイレクト
        return next({ path: `/members/${to.params.id}/profile` })
      } else {
        return next()
      }
    }
    // アクセスできないprofile画面にアクセスしようとした場合、メンバー画面にリダイレクト
    return next({ path: `/members/${to.params.id}` })
  }

  return next()
}